<script setup>
/**
 * The ground of a terrain, chosen by looking at it: the button shows the ground in use, and clicking it
 * opens the catalogue as pictures with their names, one block per family (every grass together, every
 * snow together, …). The names and the grouping come from the track format's catalogue, so a ground added
 * there is offered here with nothing else to change.
 */
import { computed, onUnmounted, ref, watch } from 'vue'
import { terrainImageSrc } from '@/lib/trackEditor/TrackSurroundings'

const props = defineProps({
  modelValue: { type: String, required: true },
  groups: { type: Array, required: true },
})
const emit = defineEmits(['update:modelValue'])

const root = ref(null)
const open = ref(false)

const terrains = computed(() => props.groups.flatMap((group) => group.terrains))
const current = computed(() => terrains.value.find((terrain) => terrain.name === props.modelValue) ?? terrains.value[0])

const textureUrl = (name) => `/img/${terrainImageSrc(name)}`

function choose(name) {
  emit('update:modelValue', name)
  open.value = false
}

/** A click anywhere else closes the list: the panel it sits in scrolls, and a list left open would follow it. */
function closeOnOutsideClick(event) {
  if (!root.value?.contains(event.target)) open.value = false
}
watch(open, (isOpen) => {
  if (isOpen) document.addEventListener('pointerdown', closeOnOutsideClick, true)
  else document.removeEventListener('pointerdown', closeOnOutsideClick, true)
})
onUnmounted(() => document.removeEventListener('pointerdown', closeOnOutsideClick, true))
</script>

<template>
  <div ref="root" class="terrain-picker">
    <button type="button" class="current" :aria-expanded="open" @click="open = !open">
      <img :src="textureUrl(current.name)" alt="">
      <span class="name">{{ current.label }}</span>
      <span class="caret" :class="{ open }">▾</span>
    </button>
    <div v-if="open" class="menu">
      <div v-for="group in groups" :key="group.label" class="group">
        <div class="group-title">{{ group.label }}</div>
        <div class="grid">
          <button v-for="terrain in group.terrains" :key="terrain.name" type="button" class="option"
            :class="{ active: terrain.name === modelValue }" :title="terrain.label"
            @click="choose(terrain.name)">
            <img :src="textureUrl(terrain.name)" alt="" loading="lazy">
            <span>{{ terrain.label }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.terrain-picker {
  font-size: 12px;
  font-family: inherit;
}

.current {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 8px 4px 4px;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #c9d1d9;
  font-family: inherit;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
}

.current:hover { border-color: #6e7681; }
.current img { width: 28px; height: 28px; object-fit: cover; border-radius: 3px; }
.current .name { flex: 1; }

.caret { color: #6e7681; transition: transform 0.15s; }
.caret.open { transform: rotate(180deg); }

.menu {
  margin-top: 6px;
  padding: 6px;
  max-height: 340px;
  overflow-y: auto;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 4px;
}

.group + .group { margin-top: 8px; }

.group-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6e7681;
  margin-bottom: 4px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));
  gap: 5px;
}

.option {
  padding: 3px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #c9d1d9;
  font-family: inherit;
  font-size: 10px;
  line-height: 1.2;
  cursor: pointer;
}

.option:hover { border-color: #388bfd; background: #1c2d5a; }
.option.active { border-color: #388bfd; background: #1c2d5a; color: #a5c8ff; }
.option img { display: block; width: 100%; height: 52px; object-fit: cover; border-radius: 3px; margin-bottom: 3px; }
.option span { display: block; overflow-wrap: anywhere; }
</style>
